import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserContext } from '../context/User';
import { ThemeContext } from '../context/Theme';
import { API, getRoleName } from '../helpers';
import {
  LayoutDashboard,
  Server,
  Key,
  GitBranch,
  FileText,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
} from 'lucide-react';

const ROUTE_PRICING_ALERT_COUNT_EVENT = 'route-pricing-alert-refresh';
const ROUTE_PRICING_ALERT_COUNT_STORAGE_KEY = 'route-pricing-alert-count';

const readStoredPricingAlertCount = () => {
  if (typeof window === 'undefined') {
    return 0;
  }
  try {
    const stored = Number(
      window.sessionStorage.getItem(ROUTE_PRICING_ALERT_COUNT_STORAGE_KEY) || 0
    );
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
};

const persistPricingAlertCount = (count) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      ROUTE_PRICING_ALERT_COUNT_STORAGE_KEY,
      String(Math.max(0, Number(count) || 0))
    );
  } catch {}
};

const Layout = ({ children }) => {
  const [userState, userDispatch] = useContext(UserContext);
  const [themeState, themeDispatch] = useContext(ThemeContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pricingAlertCount, setPricingAlertCount] = useState(() =>
    readStoredPricingAlertCount()
  );
  const roleName = getRoleName(userState.user?.role);

  const isAdmin = userState.user && userState.user.role >= 1;
  const isRoutesPage =
    location.pathname === '/routes' || location.pathname.startsWith('/routes/');

  const navItems = [
    { name: '仪表盘', path: '/', icon: LayoutDashboard, admin: true },
    { name: '供应商', path: '/provider', icon: Server, admin: true },
    { name: '令牌', path: '/token', icon: Key },
    { name: '路由', path: '/routes', icon: GitBranch, admin: true },
    { name: '日志', path: '/log', icon: FileText, admin: true },
    { name: '用户', path: '/user', icon: Users, admin: true },
    { name: '设置', path: '/setting', icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      await API.post('/api/user/logout');
    } catch {}
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    persistPricingAlertCount(0);
    window.location.href = '/login';
  };

  const toggleTheme = () => {
    themeDispatch({ type: 'toggle' });
  };

  const visibleNavItems = navItems.filter((item) => !(item.admin && !isAdmin));

  const isPathActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAdmin) {
      setPricingAlertCount(0);
      persistPricingAlertCount(0);
      return undefined;
    }
    const refreshPricingAlert = (event) => {
      const count = Number(event?.detail?.count || 0);
      const normalized = Number.isFinite(count) && count > 0 ? count : 0;
      setPricingAlertCount(normalized);
      persistPricingAlertCount(normalized);
    };
    window.addEventListener(
      ROUTE_PRICING_ALERT_COUNT_EVENT,
      refreshPricingAlert
    );
    return () => {
      window.removeEventListener(
        ROUTE_PRICING_ALERT_COUNT_EVENT,
        refreshPricingAlert
      );
    };
  }, [isAdmin]);

  const renderNavLinks = (className, onNavigate) => (
    <nav className={className}>
      {visibleNavItems.map((item) => (
        <Link
          key={item.name}
          to={item.path}
          className={`top-nav-link ${isPathActive(item.path) ? 'active' : ''}`}
          aria-current={isPathActive(item.path) ? 'page' : undefined}
          onClick={onNavigate}
        >
          <span className='top-nav-icon'>
            <item.icon size={18} />
          </span>
          <span className='top-nav-label'>{item.name}</span>
          {item.path === '/routes' && pricingAlertCount > 0 && (
            <span
              style={{
                marginLeft: '0.35rem',
                minWidth: '1.2rem',
                height: '1.2rem',
                padding: '0 0.35rem',
                borderRadius: '9999px',
                backgroundColor: 'rgba(239, 68, 68, 0.12)',
                color: 'var(--error)',
                fontSize: '0.7rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
              title={`有 ${pricingAlertCount} 个模型的当前售价低于建议售价`}
            >
              {pricingAlertCount}
            </span>
          )}
        </Link>
      ))}
    </nav>
  );

  return (
    <div className='app-shell'>
      <header className='top-nav-wrap'>
        <div className='top-nav'>
          <Link
            to='/'
            className='top-nav-brand'
            onClick={() => setSidebarOpen(false)}
          >
            <img src='/logo.png' alt='Logo' style={{ height: '1.9rem' }} />
            <span className='top-nav-brand-text'>NewAPI 网关</span>
          </Link>

          <div className='hidden md:flex top-nav-links-wrap'>
            {renderNavLinks('top-nav-links')}
          </div>

          <div className='hidden md:flex top-nav-actions'>
            <div className='top-nav-user'>
              <span className='top-nav-user-avatar'>
                {userState.user?.username?.[0]?.toUpperCase() || 'U'}
              </span>
              <span className='top-nav-user-name'>
                {userState.user?.username}
              </span>
              <span className='top-nav-user-role'>{roleName}</span>
            </div>
            <button
              onClick={toggleTheme}
              className='top-nav-action-btn'
              type='button'
            >
              {themeState.theme === 'dark' ? (
                <Sun size={16} />
              ) : (
                <Moon size={16} />
              )}
              <span>
                {themeState.theme === 'dark' ? '浅色模式' : '深色模式'}
              </span>
            </button>
            <button
              onClick={handleLogout}
              className='top-nav-action-btn'
              type='button'
            >
              <LogOut size={16} />
              <span>退出登录</span>
            </button>
          </div>

          <div className='md:hidden mobile-nav-actions'>
            <button
              onClick={toggleTheme}
              className='mobile-icon-btn'
              type='button'
              aria-label='切换主题'
            >
              {themeState.theme === 'dark' ? (
                <Sun size={20} />
              ) : (
                <Moon size={20} />
              )}
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className='mobile-icon-btn'
              type='button'
              aria-label='切换菜单'
              aria-expanded={sidebarOpen}
              aria-controls='mobile-top-menu'
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {sidebarOpen && (
        <button
          className='mobile-nav-backdrop md:hidden'
          onClick={() => setSidebarOpen(false)}
          aria-label='关闭菜单'
          type='button'
        />
      )}
      <section
        id='mobile-top-menu'
        className={`mobile-nav-panel md:hidden ${sidebarOpen ? 'open' : ''}`}
      >
        {renderNavLinks('mobile-nav-links', () => setSidebarOpen(false))}
        <div className='mobile-nav-user-card'>
          <div className='top-nav-user'>
            <span className='top-nav-user-avatar'>
              {userState.user?.username?.[0]?.toUpperCase() || 'U'}
            </span>
            <span className='top-nav-user-name'>
              {userState.user?.username}
            </span>
            <span className='top-nav-user-role'>{roleName}</span>
          </div>
          <div className='mobile-nav-actions-list'>
            <button
              onClick={toggleTheme}
              className='top-nav-action-btn'
              type='button'
            >
              {themeState.theme === 'dark' ? (
                <Sun size={16} />
              ) : (
                <Moon size={16} />
              )}
              <span>
                {themeState.theme === 'dark' ? '浅色模式' : '深色模式'}
              </span>
            </button>
            <button
              onClick={handleLogout}
              className='top-nav-action-btn'
              type='button'
            >
              <LogOut size={16} />
              <span>退出登录</span>
            </button>
          </div>
        </div>
      </section>

      <main className='app-main'>
        <div
          className={`app-content${isRoutesPage ? ' app-content-routes' : ''}`}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
